<template>
  <div>
    <div
      v-for="(blockedMappingTables, block) in conditionalMappingTables"
      :key="`mtb-${block}`"
      class="mt-1 block-group border rounded form-group"
      :class="{ 'invalid-block': getBlockError(block) }"
    >
      <div v-if="getBlockError(block)">
        <validation-errors :errors="getBlockError(block)"/>
      </div>
      <div class="flex-form-group my-1">
        <div class="flex-label small-input">
          <label for=""
          >{{ trans('dashboard.integrations.flows.condition') }}
            <hover-icon icon="question-mark-circle" :title="conditionTitle" :description="conditionDescription" hover/>
          </label>
        </div>
        <div class="flex-label tiny-input">
          <label for=""
          >{{ trans('dashboard.integrations.flows.start') }}
            <hover-icon icon="question-mark-circle" :title="startTitle" :description="startDescription" hover/>
          </label>
        </div>
        <div class="flex-label">
          <label for=""
          >{{ trans('dashboard.integrations.flows.field_name') }}
            <hover-icon icon="question-mark-circle" :title="fieldNameTitle" :description="fieldNameDescription" hover/>
          </label>
        </div>
        <div class="flex-label">
          <label for=""
          >{{ trans('dashboard.integrations.flows.operand') }}
            <hover-icon icon="question-mark-circle" :title="operandTitle" :description="operandDescription" hover/>
          </label>
        </div>
        <div class="flex-label">
          <label for=""
          >{{ trans('dashboard.integrations.flows.value') }}
            <hover-icon icon="question-mark-circle" :title="valueTitle" :description="valueDescription" hover/>
          </label>
        </div>
        <div class="flex-label">
          <label for=""
          >{{ trans('dashboard.integrations.flows.business_rules.datamodel_field') }}
            <hover-icon
              icon="question-mark-circle"
              :title="datamodelFieldActionTitle"
              :description="datamodelFieldActionDescription"
              hover
            />
          </label>
        </div>
        <div class="flex-label tiny-input">
          <label for=""
          >{{ trans('dashboard.integrations.flows.end') }}
            <hover-icon icon="question-mark-circle" :title="endTitle" :description="endDescription" hover/>
          </label>
        </div>
        <div class="flex-label">
          <label for=""
          >{{ trans('common.action') }}
            <hover-icon icon="question-mark-circle" :title="actionTitle" :description="actionDescription" hover/>
          </label>
        </div>
        <div class="flex-label">
          <label for=""
          >{{ trans('dashboard.integrations.flows.business_rules.apply_action_to') }}
            <hover-icon
              icon="question-mark-circle"
              :title="applyActionToTitle"
              :description="applyActionToDescription"
              hover
            />
          </label>
        </div>
        <div class="flex-label button-input"></div>
      </div>
      <div
        class="flex-form-group my-1"
        v-for="(mappingTable, index) in blockedMappingTables"
        :key="`mtbr-${block}-${index}`"
      >
        <div class="flex-input small-input">
          <select
            :value="mappingTable.condition"
            class="w-full form-control form-select mr-2"
            v-model="mappingTable.condition"
            @change="forceUpdateAndEmit"
            :class="{ 'is-invalid': hasError(block, index, 'condition') }"
          >
            <option v-for="option in conditions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>

        <div class="flex-input tiny-input">
          <select
            :value="mappingTable.start"
            class="w-full form-control form-select mr-2"
            v-model="mappingTable.start"
            @change="forceUpdateAndEmit"
            :class="{ 'is-invalid': hasError(block, index, 'start') }"
          >
            <option v-for="option in starts" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>

        <div class="flex-input">
          <multiselect
            v-model="mappingTable.field_name"
            :clear-on-select="false"
            :close-on-select="true"
            :options="datamodelFields"
            placeholder="Select field name"
            :preserve-search="true"
            @select="handleSelect"
            :class="{ 'is-invalid': hasError(block, index, 'field_name') }"
          />
        </div>

        <div class="flex-input">
          <select
            :value="mappingTable.operand"
            class="w-full form-control form-select mr-2"
            v-model="mappingTable.operand"
            @change="forceUpdateAndEmit"
            :class="{ 'is-invalid': hasError(block, index, 'operand') }"
          >
            <option v-for="option in operands" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>

        <div class="flex-input">
          <input
            type="text"
            class="w-full form-control form-input form-input-bordered"
            v-model="mappingTable.value"
            @input="forceUpdateAndEmit"
            :class="{ 'is-invalid': hasError(block, index, 'value') }"
          />
        </div>

        <div class="flex-input">
          <multiselect
            v-model="mappingTable.datamodel_field_action"
            :clear-on-select="false"
            :close-on-select="true"
            :options="datamodelFields"
            placeholder="Select datamodel field"
            :preserve-search="true"
            @select="handleSelect"
            :class="{ 'is-invalid': hasError(block, index, 'datamodel_field_action') }"
          />
        </div>

        <div class="flex-input tiny-input">
          <select
            :value="mappingTable.end"
            class="w-full form-control form-select mr-2"
            v-model="mappingTable.end"
            @change="forceUpdateAndEmit"
            :class="{ 'is-invalid': hasError(block, index, 'end') }"
          >
            <option v-for="option in ends" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>

        <div class="flex-input">
          <multiselect
            v-model="mappingTable.action"
            :options="actions"
            :multiple="false"
            group-values="options"
            group-label="title"
            :group-select="false"
            track-by="value"
            label="label"
            placeholder="Select action"
            :clear-on-select="false"
            :close-on-select="true"
            :preserve-search="true"
            :value="mappingTable.action"
            @select="handleSelect"
            :class="{ 'is-invalid': hasError(block, index, 'action') }"
          />
        </div>

        <div class="flex-input">
          <select
            :value="mappingTable.apply_action_to"
            class="w-full form-control form-select mr-2"
            v-model="mappingTable.apply_action_to"
            @change="forceUpdateAndEmit"
            :class="{ 'is-invalid': hasError(block, index, 'apply_action_to') }"
          >
            <option v-for="option in applyActionTo" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="flex-input button-input">
          <button
            type="button"
            class="ml-4 delete-button w-full cursor-pointer"
            style="right: 24px"
            @click="removeRow(block, index)"
          >
            X
          </button>
        </div>
      </div>
      <div class="flex mt-2 mb-3">
        <input
          :id="`${block}-rule_enabled`"
          class="form_knob_check"
          type="checkbox"
          v-model="conditionalMappingTableEnabledStates[block]"
          :class="{ 'is-invalid': hasError('rule_enabled') }"
        />
        <label :for="`${block}-rule_enabled`" class="form_knob bg-transparent">
          <span class="label-span">Enable Conditional Table Block</span>
          <hover-icon
            icon="question-mark-circle"
            :title="ruleEnabledTitle"
            :description="ruleEnabledDescription"
            hover
          />
        </label>
      </div>
      <div class="flex mt-2">
        <button type="button" @click="addRow(block)" class="button button_success">Add Row</button>
        <button type="button" @click="showConfirmationModals[block] = true" class="button button_danger">Remove Block</button>
        <button type="button" @click="moveBlock(block, 'up')" class="button button_app"
                :class="{'button_disabled': block < 1}">Move Block Up
        </button>
        <button type="button" @click="moveBlock(block, 'down')" class="button button_app"
                :class="{'button_disabled': this.conditionalMappingTables.length <= block + 1}">Move Block Down
        </button>
      </div>
      <confirmation-modal
        :show-modal="showConfirmationModals[block] ?? false"
        :header="trans('dashboard.integrations.flows.confirm_action')"
        :content="trans('dashboard.integrations.flows.business_rules.delete_conditional_table_block_confirmation_description')"
        :confirm-button-text="trans('common.confirm')"
        @close="this.showConfirmationModals[block] = false"
        @confirm="removeBlock(block)"
      />
    </div>
    <div class="flex mt-2">
      <button type="button" @click="addBlock" class="button button_success">Add Block</button>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import Multiselect from 'vue-multiselect';
import HoverIcon from '@/components/Shared/HoverIcon';
import ValidationErrors from '@/components/Shared/ValidationErrors';
import ConfirmationModal from "@/components/Dashboard/ConfirmationModal.vue";

export default {
  name: 'ConditionalMappingTable',

  components: {
    ConfirmationModal,
    Multiselect,
    HoverIcon,
    ValidationErrors,
  },

  props: {
    mappingTables: {
      type: Array,
      required: true,
    },
    id: {
      type: [Number, String],
      required: true,
    },
    errors: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      showConfirmationModals: [],
      conditionalMappingTables: [],
      conditionalMappingTableEnabledStates: [],
      conditionTitle: '<h3>Condition</h3>',
      conditionDescription:
        '<p>When a single outcome is desired for a condition that includes multiple rows a [condition] ' +
        'has to be provided. This can be set to either `OR` or `AND` and will determin if the previous row and the row ' +
        'the condition is set for should be both true to be considered `TRUE` (AND) or if only one conditional row is ' +
        'enough to evaluate to `TRUE` (OR).</p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>Conditions can only be applied when multiple rows are grouped into a single condition ' +
        'and have to be within the [start] and [end] brackets.</li></ul></div>',
      startTitle: '<h3>Start</h3>',
      startDescription:
        '<p>To group rows of conditions together to become a single condition resulting in a single outcome ' +
        'the conditional rows should be surrounded by brackets. Nested groups of conditional rows are also supported, ' +
        'making extreme complex conditions possible as well</p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>A group of (nested) conditional rows should always have an equal number of opening and ' +
        'closing brackets.</li></ul></div>',
      fieldNameTitle: '<h3>Field Name</h3>',
      fieldNameDescription:
        '<p>Select the APIcenter datamodel field name from the list, to which the condition of the row should be applied.</p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>In case the [Rule Datamodel Field] contains .*. the [Field Name] selected from the ' +
        'dropdown should be in the same array.</li></ul></div>',
      operandTitle:
        '<h3>Operand</h3>' +
        '<p>The operand determines what check should be done for each row and be applied to the value of the field selected in [Field Name].</p><br>',
      operandDescription:
        '<table class="table"><tr><th>Operand</th><th>Description</th></tr>' +
        '<tr><td>Empty</td><td>Checks if the field selected in [Field Name] is empty. (has no value)</td></tr>' +
        '<tr><td>Not Empty</td><td>Checks if the field selected in [Field Name] is not empty. (has value)</td></tr>' +
        '<tr><td>Equal</td><td>Checks if the value from the field selected in [Field Name] equals the value specified in [Value]</td></tr>' +
        '<tr><td>Not Equal</td><td>Checks if the value from the field selected in [Field Name] does not equal the value specified in [Value]</td></tr>' +
        '<tr><td>Greater</td><td>Checks if the value from the field selected in [Field Name] is greater than the value specified in [Value]</td></tr>' +
        '<tr><td>Greater or equal</td><td>Checks if the value from the field selected in [Field Name] is equal or greater than the value specified in [Value]</td></tr>' +
        '<tr><td>Less</td><td>Checks if the value from the field selected in [Field Name] is less than the value specified in [Value]</td></tr>' +
        '<tr><td>Less or equal</td><td>Checks if the value from the field selected in [Field Name] is equal or less than the value specified in [Value]</td></tr>' +
        '<tr><td>Contains (strict)</td><td>Checks if the value specified in [Value] occurs in the value from the field selected in [Field Name]. The value given will have to match exactly. including capitals</td></tr>' +
        '<tr><td>Not contains (strict)</td><td>Checks if the value specified in [Value] does not occur in the value from the field selected in [Field Name]. The value given will have to match exactly, including capitals</td></tr>' +
        '<tr><td>Contains (loose)</td><td>Checks if the value specified in [Value] occurs in the value from the field selected in [Field Name]. Before checking both [Value] and the value from the field selected in [Field Name] are made lower case.</td></tr>' +
        '<tr><td>Not contains (loose)</td><td>Checks if the value specified in [Value] does not occur in the value from the field selected in [Field Name]. Before checking both [Value] and the value from the field selected in [Field Name] are made lower case.</td></tr>' +
        '<tr><td>Is true</td><td>Checks if the value from the field selected in [Field Name] equals the boolean value `true`</td></tr>' +
        '<tr><td>Is false</td><td>Checks if the value from the field selected in [Field Name] equals the boolean value `false`</td></tr>' +
        '<tr><td>Starts with</td><td>Checks if the value from the field selected in [Field Name] starts with the value specified in [Value]</td></tr>' +
        '<tr><td>Not starts with</td><td>Checks if the value from the field selected in [Field Name] does not start with the value specified in [Value]</td></tr>' +
        '<tr><td>Ends with</td><td>Checks if the value from the field selected in [Field Name] ends with the value specified in [Value]</td></tr>' +
        '<tr><td>Not ends with</td><td>Checks if the value from the field selected in [Field Name] does not end with the value specified in [Value]</td></tr>' +
        '<tr><td>Range</td><td>Checks if the value from the field selected in [Field Name] is within the range of the values specified in [Value] (seperated by |)</td></tr>' +
        '<tr><td>Range (Numbers only)</td><td>Same as Range operand but will only use numbers from the values and the value from the field selected in [Field Name]</td></tr>' +
        '<tr><td>In List</td><td>Checks if the value from the field selected in [Field Name] is in the list of values specified in [Value] (seperated by |)</td></tr>' +
        '<tr><td>Not In List</td><td>Checks if the value from the field selected in [Field Name] is NOT in the list of values specified in [Value] (seperated by |)</td></tr>' +
        '</table>',
      valueTitle: '<h3>Value</h3>',
      valueDescription:
        '<p>The value specified will be used depending on the selected [Operand] and [Action]. For all ' +
        'operands that require a static custom value to check against the value from this column will be used. For all other ' +
        'operands the [Value] can be used for an [Action] that requires a static custom value</p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>When used for a calculation action the specified value needs to be usable as (float) to perform the action</li>' +
        '<li>When an [Action](Divide by [Value]) is selected, [Value] cannot be `0` (zero).</li>' +
        '<li>The content of [Value] is sanatized and any script and html values are removed</li></ul></div>',
      datamodelFieldActionTitle: '<h3>Datamodel Field Action</h3>',
      datamodelFieldActionDescription:
        '<p>Besides a static [Value], it is also possible to use the value from an ' +
        'APIcenter datamodel field to be used for the [Action] set. This makes it possible to apply a calculation ' +
        'depending on the outcome of a condition.</p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>When used for a calculation action the APIcenter Datamodel field should hold a value ' +
        'that can be used as (float) to perform the action dropdown should be in the same array.</li></ul></div>',
      endTitle: '<h3>End</h3>',
      endDescription:
        '<p>To group rows of conditions together to become a single condition resulting in a single outcome ' +
        'the conditional rows should be surrounded by brackets. Nested groups of conditional rows are also supported, ' +
        'making extreme complex conditions possible as well</p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>A group of (nested) conditional rows should always have an equal number of opening and ' +
        'closing brackets.</li></ul></div>',
      actionTitle: '<h3>Action</h3>',
      actionDescription:
        '<p>The [Action] specifies what should be done when the (grouped) row condition(s) is/are evaluated. ' +
        'the conditional rows should be surrounded by brackets. Nested groups of conditional rows are also supported, ' +
        'making extreme complex conditions possible as well</p><br>' +
        '<p>The actions in the dropdown are grouped by their type to make finding the correct action much easier. Besides ' +
        'that, a search function is also available that will be triggered by just starting to type in the dropdown selection ' +
        'box. Currently, the following list of groups and actions is supported and can be applied depending on the ' +
        '[Apply Action To] value to either the value from the APIcenter datamodel field selected in either [Field Name] or ' +
        '[Rule Datamodel Field]:</p><br>' +
        '<h5>Calculations:</h5>' +
        '<table class="table"><tr><th>Action</th><th>Description</th></tr>' +
        '<tr><td>Add [Value]</td><td>Adds the value from [Value] to the value from the field selected in [Apply Action To].</td></tr>' +
        '<tr><td>Divide By [Value]</td><td>Divides the value from the [Apply Action To] field by the value from [Value]. ([Value] cannot be set to zero)</td></tr>' +
        '<tr><td>Multiply with [Value]</td><td>>Multiplies the value from the [Apply Action To] field with the value from [Value].</td></tr>' +
        '<tr><td>Subtract [Value]</td><td>Substracts the value from [Value] from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Add [Datamodel Field]</td><td>Adds the value from the [Datamodel Field] to the value from the field selected in [Apply Action To].</td></tr>' +
        '<tr><td>Divide by [Datamodel Field]</td><td>Divides the value from the [Apply Action To] field by the value from the [Datamodel Field] field.</td></tr>' +
        '<tr><td>Multiply with [Datamodel Field]</td><td>Multiplies the value from the [Apply Action To] field with the value from the [Datamodel Field].</td></tr>' +
        '<tr><td>Subtract [Datamodel Field]</td><td>Substracts the value from the [Datamodel Field] from the value of the [Apply Action To] field.</td></tr>' +
        '</table>' +
        '<br><h5>String Actions:</h5>' +
        '<table class="table"><tr><th>Action</th><th>Description</th></tr>' +
        '<tr><td>Add [Value] to start</td><td>Adds the value from [Value] to the start of the value from the [Apply Action To] field.</td></tr>' +
        '<tr><td>Add [Value] to end</td><td>Adds the value from [Value] to the end of the value from the [Apply Action To] field.</td></tr>' +
        '<tr><td>Add [Datamodel Field] to start</td><td>Adds the value from [Datamodel Field] to the start of the value from the [Apply Action To] field.</td></tr>' +
        '<tr><td>Add [Datamodel Field] to end</td><td>Adds the value from [Datamodel Field] to the end of the value from the [Apply Action To] field.</td></tr>' +
        '<tr><td>Add [Datamodel Field] to start use [Value] as separator</td><td>Adds the value from [Datamodel Field] to the start of the value from the [Apply Action To] field and use the value from [Value] as separator.</td></tr>' +
        '<tr><td>Add [Datamodel Field] to end use [Value] as separator</td><td>Adds the value from [Datamodel Field] to the end of the value from the [Apply Action To] field and use the value from [Value] as separator,</td></tr>' +
        '<tr><td>Remove all occurrences of [Value]</td><td>Removes all occurrences of the value from [Value] from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Remove first occurrence of [Value]</td><td>Removes the first occurrence of the value from [Value] from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Remove last occurrence of [Value]</td><td>Removes the last occurrence of the value from [Value] from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Remove all occurrences of [Datamodel Field]</td><td>Removes all occurrences of the value from the [Datamodel Field] field from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Remove first occurrence of [Datamodel Field]</td><td>Removes the first occurrence of the value from the [Datamodel Field] field from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Remove last occurrence of [Datamodel Field]</td><td>Removes the last occurrence of the value from the [Datamodel Field] field from the value of the [Apply Action To] field.</td></tr>' +
        '<tr><td>Limit length to [Value] (from Left)</td><td>Limits the length of a string to the number specified in [Value] starting to count the length from the left side of the string.</td></tr>' +
        '<tr><td>Limit length to [Value] (from Right)</td><td>Limits the length of a string to the number specified in [Value] starting to count the length from the right side of the string.</td></tr>' +
        '</table>' +
        '<br><h5>Use A Value:</h5>' +
        '<table class="table"><tr><th>Action</th><th>Description</th></tr>' +
        '<tr><td>Use result value (TRUE/FALSE)</td><td>When selected the actual outcome of the condition, but stringified and capitalized, will be used as the result value.</td></tr>' +
        '<tr><td>Use [Rule Datamodel Field]</td><td>When the condition evaluates to `true` the value from the selected [Rule Datamodel Field] will be used as the result value for this condition.</td></tr>' +
        '<tr><td>Use [Field Name]</td><td>When the condition evaluates to `true` the value from the selected [Field Name] will be used as the result value for this condition.</td></tr>' +
        '<tr><td>Use [Value]</td><td>When the condition evaluates to `true` the value from [Value] will be used as the result value for this condition.</td></tr>' +
        '<tr><td>Use [Datamodel Field]</td><td>When the condition evaluates to `true` the value from the selected [Datamodel Field] will be used as the result value for this condition.</td></tr>' +
        '<tr><td>Use default value</td><td>When the condition evaluates to `true` the value specified as [Default Value] will be used as the result value for this condition.</td></tr>' +
        '<tr><td>Don\'t use any value</td><td>No value will be used at all when the condition evaluates to `true` or `false`</td></tr>' +
        '</table>' +
        '<br><h5>Remove Actions:</h5>' +
        '<table class="table"><tr><th>Action</th><th>Description</th></tr>' +
        '<tr><td>Remove array element</td><td>Removes the complete element from the current list being processed. Can only be used together with the [Rule Datamodel Field] which has to be a field name containing a list, recognizable by the .*. in the field name.</td></tr>' +
        '<tr><td>Remove value from [Rule Datamodel Field]</td><td>When the condition evaluates to `true` the current value in the field selected in the [Rule Datamodel Field] will be removed and set to `null`</td></tr>' +
        '</table>',
      applyActionToTitle: '<h3>Apply Action To</h3>',
      applyActionToDescription:
        '<p>Use this column to select if the action should be applied to the APIcenter datamodel field ' +
        'selected in either [Rule Datamodel Field], [Field Name] or [Datamodel Field]</p>',
      ruleEnabledTitle: '<h3>Enable Conditional Table Block</h3>',
      ruleEnabledDescription: '<p>Enable/disable Conditional Table Block</p>',
    };
  },

  beforeMount() {
    this.conditionalMappingTables = [...this.mappingTables];

    if (this.conditionalMappingTables.length === 0) {
      this.initializeMappingTables();
    }
  },

  computed: {
    ...mapGetters('flowsDetails', [
      'datamodelFields',
      'conditions',
      'actions',
      'operands',
      'starts',
      'ends',
      'applyActionTo',
    ]),
  },

  methods: {
    initializeMappingTables() {
      this.conditionalMappingTables = [[]]

      this.addRow(0);
    },

    addBlock() {
      let length = this.conditionalMappingTables.push([])
      this.addRow(length - 1);

      this.forceUpdateAndEmit();
    },

    removeBlock(index) {
      this.conditionalMappingTables.splice(index, 1)

      this.forceUpdateAndEmit();
    },

    moveBlock(index, direction) {
      const from = index
      const to = index + (direction === 'up' ? -1 : 1)

      this.conditionalMappingTables.splice(to, 0, this.conditionalMappingTables.splice(from, 1)[0])

      this.forceUpdateAndEmit()
    },

    removeRow(block, index) {
      this.conditionalMappingTables[block].splice(index, 1);

      if (this.conditionalMappingTables[block].length === 0) {
        this.removeBlock(block);
      }

      this.forceUpdateAndEmit();
    },

    addRow(block) {
      this.conditionalMappingTables[block].push({
        field_name: '',
        condition: 0,
        action: null,
        value: null,
        start: 0,
        end: 0,
        datamodel_field_action: '',
        operand: 0,
        apply_action_to: 0,
        block_enabled: true
      });

      this.forceUpdateAndEmit();
    },

    handleSelect() {
      this.forceUpdateAndEmit();
    },

    forceUpdateAndEmit() {
      this.$emit('updated', this.conditionalMappingTables);
      this.$forceUpdate();
    },

    getBlockError(block) {
      const errors = _.uniq(
        _.flatMap(_.filter(this.errors, (error, key) => key.startsWith(`conditional_tables.${block}`))),
      );

      if (errors.length > 0) {
        return errors;
      }

      return null;
    },

    hasError(block, row, field) {
      return this.errors?.[`conditional_tables.${block}.${row}.${field}`];
    },
  },

  watch: {
    conditionalMappingTables: {
      deep: true,
      handler(current) {
        this.conditionalMappingTableEnabledStates = current.map((block) => block.every((row) => row.block_enabled))
        this.showConfirmationModals = current.map(() => false)

        this.$emit('updated', current);
      },
    },

    conditionalMappingTableEnabledStates: {
      deep: true,
      handler(states) {
        states.forEach((state, block) => this.conditionalMappingTables[block].map((row) => {
          row.block_enabled = state

          return row
        }));
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.delete-button {
  font-size: 1rem;
  background-color: #d34e59;
  color: #ffffff;
  border-radius: 18px;
  text-align: center;
  line-height: 36px;
  max-width: 36px;
  max-height: 36px;
  margin-left: 0 !important;
}

.flex-form-group {
  display: flex;
  gap: 1rem;
  flex-direction: row;

  .flex-label,
  .flex-input {
    flex-basis: 20%;
  }

  .flex-label {
    display: flex;
  }

  .small-input {
    flex-basis: 10rem;
  }

  .tiny-input {
    flex-basis: 6rem;
  }

  .button-input {
    flex-basis: 4rem;
  }
}

.block-group {
  padding: 10px;
  border-radius: 10px;
}

.block-group:nth-child(even) {
  background-color: #edf9ff;
}

.invalid-block {
  border-color: #f43737 !important;
}

.label-span {
  padding-right: 5px;
}
</style>
